/**
 * What each department's workspace opens (WALLIYO-0132).
 *
 * Screen ids are the console's own (`admin/src/navigation/screens.ts`). They
 * live here rather than there because the server needs the same map: a role
 * names a department, and the department decides what that role's holders are
 * offered. A screen may appear in more than one workspace — Payments is in
 * Finance and, read-only for debugging, in Product & Engineering. It is the
 * same screen, gated by the same feature in both.
 */

import { DEPARTMENT_IDS, type Department, type DepartmentId } from "./types";

const DEPARTMENTS: Record<DepartmentId, readonly string[]> = {
  /**
   * The whole company, one line per department — and then the four places an
   * Executive actually goes: the market, the money, the accounts, the workforce.
   *
   * Every one is a screen another department owns. That is the point: the
   * Executive workspace is a way through to theirs, not a set of screens of its
   * own (WALLIYO-0134).
   */
  executive: ["executive", "marketplaceOverview", "payments", "customer", "hr", "directory", "myTimeOff", "myRequests"],

  /** The market itself: what was asked for, who can do it, what it was worth. */
  marketplace: [
    "marketplaceOverview",
    "jobs",
    "providers",
    "categories",
    "marketplaceReviews",
    "directory",
    "myTimeOff",
    "myRequests",
  ],

  /** Both sides of a conversation that has gone wrong. */
  support: ["supportOverview", "cases", "customer", "userSettings", "directory", "myTimeOff", "myRequests"],

  /** Who is who, and who may stay. */
  trust: ["trustOverview", "verifications", "reports", "directory", "myTimeOff", "myRequests"],

  /** Fees, corrections, and what somebody may hand back alone. */
  finance: ["financeOverview", "payments", "billing", "directory", "myTimeOff", "myRequests"],

  /** Getting pros on, and getting them working. */
  growth: ["growthOverview", "leads", "campaigns", "referrals", "billing", "directory", "myTimeOff", "myRequests"],

  /**
   * The platform. Accounts, Payments and Audit are listed for debugging: an
   * engineer holds the read features, a product manager does not, and the
   * screen list is the same for both.
   */
  product: [
    "productOverview",
    "incidents",
    "experiments",
    "features",
    "rules",
    "customer",
    "payments",
    "audit",
    "directory",
    "myTimeOff",
    "myRequests",
  ],

  /**
   * Hiring to leaving, and who works with whom.
   *
   * Its rows are the Human Resources screen's own tabs, not the screen: a row
   * called "Human Resources" inside the Human Resources workspace is the flat
   * console showing through. A `screen/tab` entry is one row pointing into a
   * tab of that screen (WALLIYO-0134).
   */
  people: [
    "peopleOverview",
    "hr/employees",
    "hr/recruiting",
    "hr/learning",
    "teams",
    "hr/employees/payroll",
    "directory",
    "myTimeOff",
    "myRequests",
  ],

  /** Who may do what, and the record of who decided. */
  administration: [
    "adminOverview",
    "roles",
    "permissions",
    "features",
    "actions",
    "audit",
    "directory",
    "myTimeOff",
    "myRequests",
  ],

  /** On the org chart, with nothing to open yet. */
  legal: [],
};

/**
 * What a workspace calls a screen that is not its own.
 *
 * The same screen is "Money" to an Executive, "Payments" to an Accountant and,
 * under Read for debugging, "Payments" again to an engineer. A screen has one
 * identity and more than one name, because the name belongs to whoever is
 * looking at it (WALLIYO-0134).
 *
 * Keys are console message ids; the console holds the words.
 */
const LABELS: Partial<Record<DepartmentId, Readonly<Record<string, string>>>> = {
  executive: {
    marketplaceOverview: "screen.as.market",
    payments: "screen.as.money",
    hr: "screen.as.workforce",
  },
  people: {
    "hr/employees": "screen.as.employees",
    "hr/recruiting": "screen.as.recruiting",
    "hr/learning": "screen.as.learning",
    "hr/employees/payroll": "screen.as.payroll",
  },
  product: {
    // "Accounts", under Read for debugging: an engineer opens it to answer why
    // one account behaved oddly, not to look after customers.
    customer: "screen.as.accounts",
  },
};

/** What this workspace calls that screen, or null to use the screen's own name. */
export function labelInWorkspace(id: DepartmentId, screenId: string): string | null {
  return LABELS[id]?.[screenId] ?? null;
}

export const DEPARTMENTS_BY_ID: Readonly<Record<DepartmentId, Department>> = Object.fromEntries(
  DEPARTMENT_IDS.map((id) => [id, { id, screens: DEPARTMENTS[id], hasWorkspace: DEPARTMENTS[id].length > 0 }]),
) as Record<DepartmentId, Department>;

/** Every department, in the order a switcher lists them. */
export const ALL_DEPARTMENTS: readonly Department[] = DEPARTMENT_IDS.map((id) => DEPARTMENTS_BY_ID[id]);

export function isDepartmentId(value: string): value is DepartmentId {
  return (DEPARTMENT_IDS as readonly string[]).includes(value);
}

/** The screens a workspace lists, before anybody's features are considered. */
export function screensOfDepartment(id: DepartmentId): readonly string[] {
  return DEPARTMENTS_BY_ID[id].screens;
}

/**
 * Which workspaces a set of roles opens, in department order.
 *
 * A role names one department; holding several roles is how somebody reaches
 * more than one workspace. A department with nothing to open is never offered,
 * however many roles name it.
 */
export function workspacesOfRoles(departments: readonly DepartmentId[]): readonly DepartmentId[] {
  const held = new Set(departments);
  return DEPARTMENT_IDS.filter((id) => held.has(id) && DEPARTMENTS_BY_ID[id].hasWorkspace);
}
